// ============================================================
// Stocora — Audit helpers (Phase 4, Epic 4B)
// ============================================================
// Types and pure utilities for the audit detection + action
// surface. Detection runs in Postgres (see migrations 32-34);
// actions run via the resolve_audit_finding RPC (migration 35,
// 4B.6 ticket). This file owns:
//
//   - the narrow AuditFindingKind union (kept narrower than
//     the SQL CHECK so adding a new kind surfaces as typecheck
//     failures across consumers)
//   - the AuditFindingAction union + per-kind availability
//   - email-template strings for the Email rep mailto: path
//   - the AuditFinding shape consumed by AuditPage
//   - the labelling map (display name + section sort order)
//   - small helpers for grouping / totalling / day arithmetic
//
// Ticket history:
//   4B.1 + 4B.3 (2026-05-22) — dead_stock, over_par
//   4B.2       (2026-05-22) — mismatch
//   4B.4       (2026-05-23) — expiring_soon, expired
//   4B.6       (2026-05-24) — action surface (dismiss /
//                             stop_reorder / dispose / use_now
//                             / email_rep)
//
// 4B.5 (audit page hero + tabs) will extend the page; the
// helpers in this file keep their shape.
// ============================================================

import type { Database } from '@/lib/database.types';

type AuditFindingRow = Database['public']['Tables']['audit_findings']['Row'];
type ProductRow = Database['public']['Tables']['products']['Row'];

/**
 * Subset of audit_findings.kind values that the PWA renders.
 * Narrower than the SQL CHECK so adding a new kind surfaces
 * as a typecheck failure in this file rather than a silent
 * fall-through to the "unknown kind" path in the page.
 */
export type AuditFindingKind =
  | 'dead_stock'
  | 'over_par'
  | 'mismatch'
  | 'expiring_soon'
  | 'expired';

/**
 * Action types accepted by the resolve_audit_finding RPC.
 * Mirror in the SQL CASE in
 * 20260524120000_audit_actions_4b6.sql.
 *
 * - dismiss:      close without side effect, no cascade
 * - stop_reorder: set products.do_not_reorder = true, resolve,
 *                 cascade to sibling open findings
 * - dispose:      write off via inventory_events, resolve,
 *                 cascade
 * - use_now:      acknowledge "I'll address this," resolve,
 *                 cascade. No side effect on inventory or
 *                 product state. Available for expiring_soon
 *                 only.
 * - email_rep:    NOT an RPC action — handled entirely in the
 *                 client via mailto:. Included here for typing
 *                 the per-kind availability map. The PWA
 *                 doesn't call the RPC for this; the finding
 *                 stays open until the operator separately
 *                 dismisses or resolves.
 */
export type AuditFindingAction =
  | 'dismiss'
  | 'stop_reorder'
  | 'dispose'
  | 'use_now'
  | 'email_rep';

/**
 * The full audit_findings row as PostgREST returns it, with
 * `kind` re-typed to the narrowed union. Rows whose SQL `kind`
 * value falls outside the union are filtered out at load time
 * (defensive — should never happen given DB-side CHECK).
 */
export interface AuditFinding {
  readonly id: string;
  readonly clinic_id: string;
  readonly product_id: string;
  readonly kind: AuditFindingKind;
  readonly capital_tied_up: number;
  readonly status: AuditFindingRow['status'];
  readonly detected_at: string;
  readonly dismissed_at: AuditFindingRow['dismissed_at'];
  readonly dismissed_by: AuditFindingRow['dismissed_by'];
  readonly notes: AuditFindingRow['notes'];
}

/**
 * Pared-down product row shape used by AuditPage rows.
 *
 * - mismatch_note (4B.2): operator-supplied "why," surfaced
 *   inline on mismatch findings
 * - expiration_date (4B.4): drives the days-until / days-ago
 *   suffix on expiring_soon and expired findings
 * - rep_email (2026-07-07): the address for the Email rep action.
 *   NOT a products column — it is resolved from the product's
 *   vendor (vendors.rep_email) at fetch time in AuditPage and
 *   attached here. Nullable: when the vendor has no rep on file,
 *   the Email rep action is disabled (see actionsForKind).
 */
export type AuditProduct = Pick<
  ProductRow,
  'id' | 'name' | 'count' | 'par' | 'unit_price' | 'uom'
  | 'mismatch_note' | 'expiration_date'
> & {
  /** Resolved from vendors.rep_email via the product's vendor. */
  readonly rep_email: string | null;
};

/**
 * A finding joined with its product, ready for rendering.
 */
export interface AuditFindingWithProduct {
  readonly finding: AuditFinding;
  readonly product: AuditProduct;
}

/**
 * Display metadata per kind. `order` drives section ordering
 * on AuditPage (lower = higher up).
 *
 *   1 dead_stock     algorithmic; consumption signal
 *   2 over_par       algorithmic; pace signal
 *   3 mismatch       operator-flagged
 *   4 expired        time; most urgent within time bucket
 *   5 expiring_soon  time; still actionable
 */
export interface AuditKindMeta {
  readonly label: string;
  readonly shortLabel: string;
  readonly order: number;
}

export const LABEL_BY_KIND: Record<AuditFindingKind, AuditKindMeta> = {
  dead_stock:    { label: 'Dead stock',    shortLabel: 'Dead',     order: 1 },
  over_par:      { label: 'Over par',      shortLabel: 'Over',     order: 2 },
  mismatch:      { label: 'Mismatch',      shortLabel: 'Mismatch', order: 3 },
  expired:       { label: 'Expired',       shortLabel: 'Expired',  order: 4 },
  expiring_soon: { label: 'Expiring soon', shortLabel: 'Soon',     order: 5 },
};

const VALID_KINDS: ReadonlySet<string> = new Set<AuditFindingKind>([
  'dead_stock',
  'over_par',
  'mismatch',
  'expired',
  'expiring_soon',
]);

export function narrowAuditFinding(
  row: AuditFindingRow,
): AuditFinding | null {
  if (!VALID_KINDS.has(row.kind)) return null;
  return {
    id: row.id,
    clinic_id: row.clinic_id,
    product_id: row.product_id,
    kind: row.kind as AuditFindingKind,
    capital_tied_up: Number(row.capital_tied_up),
    status: row.status,
    detected_at: row.detected_at,
    dismissed_at: row.dismissed_at,
    dismissed_by: row.dismissed_by,
    notes: row.notes,
  };
}

// ============================================================
// Display lenses (Block D, 2026-07-14) — recoverable-capital merge
// ============================================================
// PRESENTATION-ONLY per the Block-D decision. The DB keeps
// dead_stock and mismatch as distinct kinds (finding-origin
// history stays queryable); the AUDIT PAGE groups them into one
// "Recoverable capital" lens, showing each product once tagged
// with WHY it's recoverable. Everything that isn't dead_stock or
// mismatch maps to its own lens 1:1.
//
// This is what replaces the old per-kind tiles + the overlap
// footnote: because the recoverable lens de-dups per product, its
// subtotal is honest (each product counted once) and the tiles no
// longer sum to more than the hero total.
//
// UPGRADE TRIGGER (mirrored in the detector migration): if a
// direct audit_findings consumer is ever scheduled, the merge
// graduates to a detector-level `recoverable_capital` kind. Until
// then, this lens layer is the single place the collapse happens.
//
// Individual rows still render off their real finding.kind (meta
// line + action set), so nothing about FindingRow changes — only
// the grouping/tile/tab layer sees lenses.

export type AuditLens =
  | 'recoverable_capital'
  | 'over_par'
  | 'expired'
  | 'expiring_soon';

export interface AuditLensMeta {
  readonly label: string;
  readonly shortLabel: string;
  readonly order: number;
}

export const LABEL_BY_LENS: Record<AuditLens, AuditLensMeta> = {
  recoverable_capital: { label: 'Recoverable capital', shortLabel: 'Recoverable', order: 1 },
  over_par:            { label: 'Over par',            shortLabel: 'Over',        order: 2 },
  expired:             { label: 'Expired',             shortLabel: 'Expired',     order: 3 },
  expiring_soon:       { label: 'Expiring soon',       shortLabel: 'Soon',        order: 4 },
};

/** Which display lens a raw finding kind belongs to. */
export function lensForKind(kind: AuditFindingKind): AuditLens {
  if (kind === 'dead_stock' || kind === 'mismatch') return 'recoverable_capital';
  return kind;
}

/**
 * Short "why it's recoverable" tag for a finding inside the
 * recoverable_capital lens. Derives from the kind: mismatch is
 * operator-flagged (discontinued system / retired technique),
 * dead_stock is algorithmic (slow-moving). Returns null for kinds
 * that aren't part of the recoverable lens.
 */
export function reasonTagForFinding(
  item: AuditFindingWithProduct,
): string | null {
  switch (item.finding.kind) {
    case 'dead_stock':
      return 'Slow-moving';
    case 'mismatch':
      // Prefer the operator's own note when present; else a generic tag.
      return item.product.mismatch_note ? 'Discontinued' : 'Flagged';
    default:
      return null;
  }
}

/**
 * Group findings by display lens, ordered by LABEL_BY_LENS.order.
 *
 * The recoverable_capital lens is DE-DUPED per product: a product
 * that trips both dead_stock and mismatch appears once, keeping the
 * finding with the higher capital_tied_up (matching distinctCapital's
 * max rule). The dropped finding is still open in the DB and its
 * actions still resolve it via the cross-kind cascade — we just
 * don't render a second row for the same product in this lens.
 *
 * All other lenses pass through unchanged (one finding = one row).
 */
export function groupByLens(
  findings: readonly AuditFindingWithProduct[],
): readonly { lens: AuditLens; rows: readonly AuditFindingWithProduct[] }[] {
  const buckets = new Map<AuditLens, AuditFindingWithProduct[]>();
  for (const item of findings) {
    const lens = lensForKind(item.finding.kind);
    let bucket = buckets.get(lens);
    if (!bucket) {
      bucket = [];
      buckets.set(lens, bucket);
    }
    bucket.push(item);
  }

  // De-dup the recoverable lens per product (keep max-capital finding).
  const recoverable = buckets.get('recoverable_capital');
  if (recoverable) {
    const bestByProduct = new Map<string, AuditFindingWithProduct>();
    for (const item of recoverable) {
      const pid = item.finding.product_id;
      const prev = bestByProduct.get(pid);
      if (!prev || item.finding.capital_tied_up > prev.finding.capital_tied_up) {
        bestByProduct.set(pid, item);
      }
    }
    buckets.set('recoverable_capital', Array.from(bestByProduct.values()));
  }

  const out: { lens: AuditLens; rows: readonly AuditFindingWithProduct[] }[] = [];
  for (const lens of (Object.keys(LABEL_BY_LENS) as AuditLens[]).sort(
    (a, b) => LABEL_BY_LENS[a].order - LABEL_BY_LENS[b].order,
  )) {
    const rows = buckets.get(lens);
    if (rows && rows.length > 0) out.push({ lens, rows });
  }
  return out;
}


export function totalCapital(
  findings: readonly AuditFindingWithProduct[],
): number {
  let sum = 0;
  for (const item of findings) sum += item.finding.capital_tied_up;
  return sum;
}

/**
 * Hero-total variant that counts each PRODUCT once, even when it
 * carries open findings under multiple kinds.
 *
 * Why this exists (2026-07-07): dead_stock and mismatch both
 * compute capital_tied_up = count * unit_price, and the demo seed
 * makes the same products trip both detectors (see the overlap
 * note in 20260522120000_audit_detection_mismatch.sql). Summing
 * every finding therefore double-counts that capital in the hero
 * figure — verified on the demo clinic: sum-all = $64,954 vs.
 * distinct = $32,477 for the identical inventory.
 *
 * totalCapital() is UNCHANGED and still used for the per-kind
 * subtotals (each kind's row is genuinely worth its own sum). Only
 * the single top-line "Capital tied up" number uses this. As a
 * result the per-kind breakdown can add up to MORE than this
 * total; the hero renders a footnote explaining the overlap.
 *
 * De-dup rule: a product's contribution is the MAX capital_tied_up
 * across its open findings (not the sum, not the first). Max keeps
 * the headline conservative-but-honest — the largest single claim
 * on that product's capital, never inflated by re-counting it.
 *
 * If detectors are later made mutually exclusive (the classification
 * fork for Drew), each product will carry at most one open finding
 * and this collapses to equal totalCapital() — safe to keep either
 * way.
 */
export function distinctCapital(
  findings: readonly AuditFindingWithProduct[],
): number {
  const maxByProduct = new Map<string, number>();
  for (const item of findings) {
    const productId = item.finding.product_id;
    const prev = maxByProduct.get(productId) ?? 0;
    if (item.finding.capital_tied_up > prev) {
      maxByProduct.set(productId, item.finding.capital_tied_up);
    }
  }
  let sum = 0;
  for (const value of maxByProduct.values()) sum += value;
  return sum;
}

const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export function formatCapital(amount: number): string {
  return USD_FORMATTER.format(amount);
}

// ---- Expiry helpers (4B.4) ----------------------------------

const MS_PER_DAY = 86_400_000;

export function daysFromToday(isoDate: string | null | undefined): number | null {
  if (!isoDate) return null;
  const target = new Date(isoDate).getTime();
  if (Number.isNaN(target)) return null;
  return Math.round((target - Date.now()) / MS_PER_DAY);
}

export function formatExpiryContext(
  kind: 'expiring_soon' | 'expired',
  days: number | null,
): string {
  if (days === null) {
    return kind === 'expired' ? 'expired' : 'expires soon';
  }
  if (kind === 'expired') {
    const ago = Math.abs(days);
    if (ago === 0) return 'expired today';
    if (ago === 1) return 'expired yesterday';
    return `expired ${ago}d ago`;
  }
  if (days <= 0) return 'expires today';
  if (days === 1) return 'expires tomorrow';
  return `expires in ${days}d`;
}

// ---- Action surface (4B.6) ----------------------------------

/**
 * UI metadata for an action button. Maps to the
 * AuditFindingAction union; `tone` drives button color
 * treatment in AuditPage (green for resolve, red for
 * destructive, slate for neutral).
 */
export interface AuditActionMeta {
  readonly action: AuditFindingAction;
  readonly label: string;
  readonly tone: 'neutral' | 'primary' | 'destructive';
  /** True if the action requires a confirmation modal. */
  readonly confirms: boolean;
  /** True if the action is handled client-side (no RPC call). */
  readonly clientOnly: boolean;
}

const ACTION_META: Record<AuditFindingAction, AuditActionMeta> = {
  email_rep: {
    action: 'email_rep',
    label: 'Email rep',
    tone: 'neutral',
    confirms: false,
    clientOnly: true,
  },
  use_now: {
    action: 'use_now',
    label: 'Use it now',
    tone: 'primary',
    confirms: false,
    clientOnly: false,
  },
  stop_reorder: {
    action: 'stop_reorder',
    label: 'Stop reordering',
    tone: 'primary',
    confirms: false,
    clientOnly: false,
  },
  dispose: {
    action: 'dispose',
    label: 'Dispose / write off',
    tone: 'destructive',
    confirms: true,
    clientOnly: false,
  },
  dismiss: {
    action: 'dismiss',
    label: 'Dismiss',
    tone: 'neutral',
    confirms: false,
    clientOnly: false,
  },
};

/**
 * Per-kind action availability. Mirrors the SQL applicability
 * guards in resolve_audit_finding(). If the two ever drift,
 * the RPC's 22013 error code is the safety net but the user
 * should never see it because the PWA only renders applicable
 * buttons.
 *
 * Order in each array drives display order in the action bar.
 * Convention: Email rep first (always available, client-only),
 * then kind-specific actions, Dismiss last (always available,
 * lowest commitment).
 */
const ACTIONS_BY_KIND: Record<AuditFindingKind, readonly AuditFindingAction[]> = {
  dead_stock:    ['email_rep', 'stop_reorder', 'dispose', 'dismiss'],
  over_par:      ['email_rep', 'stop_reorder', 'dismiss'],
  mismatch:      ['email_rep', 'stop_reorder', 'dispose', 'dismiss'],
  expiring_soon: ['email_rep', 'use_now', 'dismiss'],
  expired:       ['email_rep', 'dispose', 'dismiss'],
};

export function actionsForKind(kind: AuditFindingKind): readonly AuditActionMeta[] {
  return ACTIONS_BY_KIND[kind].map((a) => ACTION_META[a]);
}

/**
 * True when the product's vendor has a rep email on file. The
 * Email rep action is gated on this: with no address there's
 * nothing to send to, so the button is hidden rather than
 * producing a recipient-less draft (decided 2026-07-07). The
 * empty state points the operator to Settings › Vendors.
 */
export function hasRepEmail(product: AuditProduct): boolean {
  return !!product.rep_email && product.rep_email.trim() !== '';
}

